<template>
  <div class="relative h-full w-full overflow-y-auto">
    <div class="flex flex-col overflow-hidden border-b border-slate-200">
      <div
        class="px-4 py-3 text-slate-900 font-semibold text-lg grid grid-cols-[auto_1fr_auto] items-center gap-4"
      >
        <span>Searches</span>
        <div class="col-start-3 flex items-center gap-3">
          <button
            class="text-sm font-medium text-blue-600 hover:text-blue-700 transition"
            @click="toggleAllExpanded"
          >
            <i-material-symbols-collapse-all
              v-if="isAllExpanded"
              v-tooltip="'Collapse all'"
              class="h-5 w-5 text-slate-900"
            />
            <i-material-symbols-expand-all
              v-else
              v-tooltip="'Expand all'"
              class="h-5 w-5 text-slate-900"
            />
          </button>
        </div>
      </div>
    </div>
    <div class="flex items-center text-sm font-medium py-2.5 px-4">
      <span class="flex-1 min-w-0">Source</span>
      <div class="flex items-center flex-shrink-0">
        <span class="w-48">Query</span>
        <span class="w-20 text-center">Results</span>
        <span class="w-16"></span>
      </div>
    </div>
    <TransitionGroup name="flash">
      <div v-for="importSource in importSources" :key="importSource?.id" class="break-inside-avoid">
        <ImportSourceCard
          v-if="
            importSource?.id &&
            (isBuiltInImportSource(importSource.id) ||
              importSource.type === ImportSourceType.FIELD_SAFETY_NOTICES) &&
            ![
              BuiltInImportSourceId.HAND_SEARCH as string,
              BuiltInImportSourceId.CITATION_SEARCH as string,
            ].includes(importSource.id)
          "
          :import-source="importSource"
          :icon-url="importSource.iconUrl"
          :query="generateSearchQuery(importSource.id, review.entity.value)"
          :is-expanded="expandedSourceIds.has(importSource.id)"
          @toggle-expanded="handleToggleExpanded(importSource.id, $event)"
        />

        <ImportFromCustomSource
          v-else-if="
            ![BuiltInImportSourceId.HAND_SEARCH, BuiltInImportSourceId.CITATION_SEARCH].includes(
              importSource.id as BuiltInImportSourceId,
            )
          "
          :import-source="importSource"
          :is-expanded="expandedSourceIds.has(importSource.id)"
          @toggle-expanded="handleToggleExpanded(importSource.id, $event)"
        />
      </div>
      <HandSearch
        v-if="
          review.entity.value.plan?.importPlan.importSources?.some(
            (i) => i.id === BuiltInImportSourceId.HAND_SEARCH,
          )
        "
        key="hand-search"
        :is-expanded="expandedSourceIds.has(BuiltInImportSourceId.HAND_SEARCH)"
        @toggle-expanded="handleToggleExpanded(BuiltInImportSourceId.HAND_SEARCH, $event)"
      />
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from "@app/utils/injectStrict";
import { generateSearchQuery } from "@app/utils/generateSearchQuery/generateSearchQuery";
import { ReviewKey } from "../../../use-review";
import { computed, ref } from "vue";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import ImportFromCustomSource from "./CustomImportSource/ImportFromCustomSource.vue";
import HandSearch from "./HandSearch/HandSearch.vue";
import useBuiltInImportSources from "@app/composables/use-built-in-import-sources";
import ImportSourceCard from "../Components/ImportSourceCard.vue";
import { SafetyInformationSourcesKey } from "@app/injectionKeys";
import { ImportSourceType } from "@core/domain/types/import-source-type.type";

const review = injectStrict(ReviewKey);
const builtInImportSources = useBuiltInImportSources();
const safetyInformationSources = injectStrict(SafetyInformationSourcesKey);
const expandedSourceIds = ref<Set<string>>(new Set());

function handleToggleExpanded(sourceId: string, event?: MouseEvent) {
  window.getSelection()?.removeAllRanges();
  const isShift = event?.shiftKey || false;
  if (isShift) {
    if (expandedSourceIds.value.has(sourceId)) {
      expandedSourceIds.value.delete(sourceId);
    } else {
      expandedSourceIds.value.add(sourceId);
    }
    expandedSourceIds.value = new Set(expandedSourceIds.value);
  } else {
    if (expandedSourceIds.value.has(sourceId)) {
      expandedSourceIds.value.delete(sourceId);
      expandedSourceIds.value = new Set(expandedSourceIds.value);
    } else {
      expandedSourceIds.value = new Set([sourceId]);
    }
  }
}

const expandableSourceIds = computed(() => {
  const ids =
    review.entity.value.plan?.importPlan.importSources
      ?.map((s) => s.id)
      .filter(
        (id) => ![BuiltInImportSourceId.CITATION_SEARCH].includes(id as BuiltInImportSourceId),
      ) ?? [];

  if (
    review.entity.value.plan?.importPlan.importSources?.some(
      (i) => i.id === BuiltInImportSourceId.HAND_SEARCH,
    )
  ) {
    ids.push(BuiltInImportSourceId.HAND_SEARCH);
  }

  return ids;
});

const isAllExpanded = computed(() => {
  return (
    expandableSourceIds.value.length > 0 &&
    expandableSourceIds.value.every((id) => expandedSourceIds.value.has(id))
  );
});

function toggleAllExpanded() {
  if (isAllExpanded.value) {
    expandedSourceIds.value.clear();
    expandedSourceIds.value = new Set();
  } else {
    expandedSourceIds.value = new Set(expandableSourceIds.value);
  }
}

const importSources = computed(() => {
  return review.entity.value.plan?.importPlan.importSources
    ?.map((s) => {
      const source = { ...s };
      const builtInSource = builtInImportSources.findById(source.id);

      if (builtInSource) {
        source.iconUrl = builtInSource.iconUrl;
      } else {
        const safetyInformationSource = safetyInformationSources.find((x) => x.id === source.id);
        if (safetyInformationSource) {
          source.iconUrl = safetyInformationSource.country.flagUrl;
        }
      }

      return source;
    })
    .sort((a, b) => a.order - b.order)
    .sort((a, b) => {
      const aLength = review.searchesBySource.value?.[a.id]?.length || 0;
      const bLength = review.searchesBySource.value?.[b.id]?.length || 0;

      if (aLength > 0 && bLength === 0) return -1;
      if (aLength === 0 && bLength > 0) return 1;
      return 0;
    });
});

const isBuiltInImportSource = (importSourceId: string): importSourceId is BuiltInImportSourceId => {
  return Object.values(BuiltInImportSourceId).includes(importSourceId as BuiltInImportSourceId);
};
</script>
